"use client";

import * as React from "react";
import Link from "next/link";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { LATEST_RELEASE } from "@/lib/changelog";
import { hasSeenLocally, markChangelogSeen } from "@/lib/changelog-seen";
import { ReleaseHighlights, ReleaseChanges } from "@/components/changelog/release-content";

/**
 * Auto-opens once when the newest release is `major` and the current user
 * hasn't seen it. "Seen" is checked against both the server value (`lastSeen`,
 * from auth metadata) and localStorage (see lib/changelog-seen). Dismissing —
 * or visiting /changelog — records the version so it never returns.
 *
 * The open decision runs in an effect (not useState init) so it can read
 * localStorage without an SSR/hydration mismatch.
 */
export function WhatsNew({ lastSeen }: { lastSeen: string | null }) {
  const latest = LATEST_RELEASE;
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!latest.major) return;
    if (lastSeen === latest.version) return;
    if (hasSeenLocally(latest.version)) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- opens once after a client-only localStorage check (deferred to avoid SSR/hydration mismatch)
    setOpen(true);
  }, [latest.major, latest.version, lastSeen]);

  function dismiss() {
    setOpen(false);
    void markChangelogSeen(latest.version);
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) dismiss();
      }}
      title="What's new"
      description={`${latest.title} · v${latest.version}`}
      size="md"
      footer={
        <>
          <Button asChild variant="outline" onClick={dismiss}>
            <Link href="/changelog">Full changelog</Link>
          </Button>
          <Button onClick={dismiss}>Got it</Button>
        </>
      }
    >
      <div className="space-y-4">
        {latest.highlights?.length ? <ReleaseHighlights highlights={latest.highlights} /> : null}
        <ReleaseChanges changes={latest.changes} />
      </div>
    </Modal>
  );
}
